import React, { useState } from 'react'
import Avatar from './Avatar'
import Gap from './Gap'
import ProfilePhotoViewer from './ProfilePhotoViewer'
import { TRAIT_LABEL, type Trait } from '../data/quiz'

export interface OtherUserProfileInfoProps {
  nickname: string
  profileImage?: string
  username: string
  bio?: string | null // 한 줄 소개
  consecutiveAttendanceDays?: number | null
  /**
   * 취향분석에서 가장 높게 나온 성향 코드('trend' 등). 안 풀었으면 없다.
   *
   * 서버는 코드로 내려주고 보이는 이름은 여기서 옮긴다 — '웨이팅'을 '검증'으로
   * 바꿀 때처럼, 이름이 바뀌어도 서버와 저장된 응답은 건드리지 않는다.
   */
  tasteTrait?: string | null
  /** 함께한 밥약 횟수. **맞팔일 때만** 보여준다 */
  mealmateCount?: number | null
  /** 서로 팔로우 중인가. 아니면 밥약을 숨긴다 */
  isMutualFollow?: boolean
  /** true면 프로필 영역 첫 줄에 닉네임 표시 (마이·타인 프로필: 상단·앱바는 아이디, 프로필 옆은 닉네임) */
  emphasizeNicknameInProfileRow?: boolean
  isOtherUserProfile?: boolean
}

const OtherUserProfileInfo: React.FC<OtherUserProfileInfoProps> = ({
  nickname,
  profileImage,
  username,
  bio,
  consecutiveAttendanceDays,
  tasteTrait,
  mealmateCount,
  isMutualFollow = false,
  emphasizeNicknameInProfileRow = false,
  isOtherUserProfile = false,
}) => {
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false)

  /*
   * 연속 출석(streak) 표시는 **잠시 꺼 뒀다** — 그 자리를 취향 뱃지에 내줬다.
   * 지우지 않고 남긴다. 되살릴 때 다시 짜지 않으려는 것이다.
   *
   *   // 연속 출석 일수는 항상 일 단위로 표시 (7일 이후에도 주로 환산하지 않음)
   *   const getAttendanceMessage = (days?: number | null): string => {
   *     if (!days || days < 1) return '연속 출석 중이지 않습니다'
   *     return `${days}일 연속 출석 중!`
   *   }
   *   const attendanceMessage = getAttendanceMessage(consecutiveAttendanceDays)
   */

  return (
    <div className="w-full flex items-center">
      {/* 썸네일 (탭하면 인스타그램처럼 확대 미리보기) */}
      <Avatar
        nickname={nickname}
        profileImage={profileImage}
        size={76}
        onClick={() => setIsPhotoViewerOpen(true)}
      />

      <ProfilePhotoViewer
        open={isPhotoViewerOpen}
        profileImage={profileImage}
        nickname={nickname}
        onClose={() => setIsPhotoViewerOpen(false)}
      />

      <Gap width={12} />

      <div className="flex-1 flex flex-col items-start">
        {/* 닉네임 / 아이디 (마이 페이지에서는 상단에 아이디, 여기는 닉네임) */}
        <span className="font-pretendard font-700 text-16px leading-125% text-brown-80%-main">
          {emphasizeNicknameInProfileRow ? nickname : username}
        </span>

        {/*
         * 예전에는 여기(닉네임 바로 아래)에 밥약·연속 출석이 왔다. 지금은
         * 취향 뱃지와 **한 줄에 나란히** 두려고 한 줄 소개 아래로 내렸다.
         */}
        {isOtherUserProfile ? null : (
          /*
           * 연속 출석(streak)은 **잠시 숨긴다** — 그 자리를 취향 뱃지에 내줬다.
           * 지우지 않고 주석으로 남긴다. 되살릴 때 다시 짜지 않으려는 것이다.
           *
           *   !!consecutiveAttendanceDays && consecutiveAttendanceDays >= 1 && (
           *     <> <Gap height={4} />
           *       <div className="flex gap-4px items-center">
           *         <img src="/images/leaf-icon.png" alt="leaf" className="w-20px h-20px object-contain" />
           *         <span className="text-ellipsis-1 text-style-button2 leading-none text-greenGreen500">
           *           {attendanceMessage}
           *         </span>
           *       </div>
           *     </>
           *   )
           */
          null
        )}

        {/* 한 줄 소개 */}
        {bio && (
          <>
            <Gap height={4} />
            <span className="text-style-body2 text-textColorText2 text-ellipsis-1">
              {bio}
            </span>
          </>
        )}

        {/*
         * 취향 뱃지. 한 줄 소개 아래에 온다.
         *
         * 모르는 성향 코드가 오면(서버에 새 유형이 생겼는데 앱이 옛 버전)
         * 아무것도 안 그린다 — 코드가 그대로 노출되는 것보다 낫다.
         */}
        {(() => {
          /*
           * 취향 뱃지는 **잠시 숨긴다**(2026-10-29, 도연님).
           *
           * 테스트 결과를 계정에 붙이는 일은 그대로 둔다 — 서버가 user_id 를
           * 적고 taste_trait 도 내려준다. 화면에만 안 띄운다. 결과가 아직
           * 정확하지 않다는 피드백이 있어 문항을 손본 뒤 다시 켠다.
           *
           * 되살리려면 아래 false 를 지우면 된다.
           */
          const SHOW_TASTE_BADGE = false
          const badge =
            SHOW_TASTE_BADGE && tasteTrait && TRAIT_LABEL[tasteTrait as Trait]
          // 밥약은 **맞팔일 때만**. 한쪽만 팔로우한 사이에 둘이 몇 번 같이
          // 먹었는지 알려 줄 이유가 없다.
          const showMealmate = isOtherUserProfile && isMutualFollow
          if (!badge && !showMealmate) return null

          return (
            <>
              <Gap height={4} />
              {/* 둘 다 있으면 나란히. 좁으면 아래로 접힌다 */}
              <div className="flex flex-wrap items-center gap-x-12px gap-y-4px">
                {badge && (
                  <div className="flex gap-4px items-center">
                    <span className="text-style-button2 leading-none text-brown-80%-main">
                      ✷
                    </span>
                    <span className="text-ellipsis-1 text-style-button2 leading-none text-brown-80%-main">
                      {TRAIT_LABEL[tasteTrait as Trait]}
                    </span>
                  </div>
                )}
                {showMealmate && (
                  <div className="flex gap-6px items-center">
                    <img
                      src="/images/mealmate-icon.png"
                      alt="밥약"
                      className="w-11px h-20px object-contain"
                    />
                    <span className="text-ellipsis-1 text-style-button2 leading-none text-brown-80%-main">
                      함께한 밥약 : {mealmateCount ?? 0} 번
                    </span>
                  </div>
                )}
              </div>
            </>
          )
        })()}
      </div>
    </div>
  )
}

export default OtherUserProfileInfo
